import { Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { NewsService } from '../../../shared/services/news/news.service';
import { CustomPipe } from '../../../shared/pipes/custom.pipe';
import { MatButtonModule } from '@angular/material/button';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { UsersService } from '../../../shared/services/users/users.service';
import { LoaderComponent } from '../../../shared/components/loader/loader.component';
import { LoaderService } from '../../../shared/services/loader/loader.service';
import { MatTabsModule } from '@angular/material/tabs';
import { forkJoin, Subject } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';
import { QuillModule } from 'ngx-quill';
import { QuillConfigService } from '../../../shared/services/quill-config.service';
import { RolesService } from '../../../shared/services/roles/roles.service';
import { StorageService } from '../../../shared/services/storage/storage.service';
import { TimezoneAdjustPipe } from 'src/app/shared/pipes/timezone-adjust.pipe';

@Component({
  selector: 'app-edit-detail-mail',
  standalone: true,
  imports: [
    MatIconModule,
    MatFormFieldModule,
    MatSelectModule,
    ReactiveFormsModule,
    MatInputModule,
    CustomPipe,
    MatButtonModule,
    MatPaginatorModule,
    MatTableModule,
    MatSortModule,
    RouterLink,
    LoaderComponent,
    MatTabsModule,
    QuillModule,
    TimezoneAdjustPipe,
  ],
  templateUrl: './edit-detail-mail.component.html',
  styleUrls: [
    './edit-detail-mail.component.css',
    '../../../shared/styles/layout.css',
  ],
})
export class EditDetailMailComponent implements OnInit, OnDestroy {
  modules: any;
  myroles: any = {};
  element: any = {}; // Initialize as empty object, not null

  // Form group for the suggestion form
  suggestionForm!: FormGroup;

  // For direct access in template
  get toppings() {
    return this.suggestionForm.get('toppings');
  }
  get observation() {
    return this.suggestionForm.get('observation');
  }

  toppingList: string[] = ['Iniciada', 'En trámite', 'Aceptada', 'Declinada'];

  // Define the columns to display
  displayedColumns: string[] = [
    'observation',
    'new_status',
    'updated_status_date',
  ];
  dataSource: MatTableDataSource<any> = new MatTableDataSource<any>();

  private _snackBar = inject(MatSnackBar);

  // Función para mostrar un snackbar
  openSnackBar(message: string) {
    this._snackBar.open(message, 'Cerrar', {
      duration: 5000,
    });
  }

  private _liveAnnouncer = inject(LiveAnnouncer);
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  private destroy$ = new Subject<void>();

  announceSortChange(sortState: Sort) {
    if (sortState.direction) {
      this._liveAnnouncer.announce(`Sorted ${sortState.direction}ending`);
    } else {
      this._liveAnnouncer.announce('Sorting cleared');
    }
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  constructor(
    private route: ActivatedRoute,
    private newsService: NewsService,
    private usersService: UsersService,
    private router: Router,
    private loaderService: LoaderService,
    private fb: FormBuilder,
    private quillConfigService: QuillConfigService,
    private rolesService: RolesService,
    private storageService: StorageService
  ) {
    this.modules = this.quillConfigService.getDefaultModules();
    // Initialize the form with validators
    this.initForm();
  }

  /**
   * Initialize the form with validators
   */
  private initForm(): void {
    this.suggestionForm = this.fb.group({
      toppings: ['', Validators.required],
      observation: ['', [Validators.required]],
    });
  }

  get isLoading() {
    return this.loaderService.getLoader();
  }

  loadRoles() {
    // Get permissions using the service method
    const permissions = this.rolesService.getPermissionsForFunctionality(
      'inbox',
      this.storageService
    );

    if (permissions) {
      // Store permissions in myroles
      this.myroles = permissions;

      // Check write permission and redirect if needed
      this.rolesService.checkWritePermission(
        'inbox',
        this.storageService,
        this._snackBar,
        this.router,
        '/home/communication/mailbox',
        'No tienes permisos para editar una consulta'
      );
    } else {
      // console.log('No user roles found in storage');
    }
  }

  ngOnInit(): void {
    // Show loader immediately when component initializes
    this.loaderService.showLoader();

    // Call methods with forkJoin to handle multiple API calls
    this.loadData();
    this.loadRoles();
  }

  /**
   * Loads all necessary data using forkJoin for better coordination
   */
  private loadData(): void {
    this.route.params.subscribe((params) => {
      const suggestionId = params['id'];

      // Use forkJoin to coordinate multiple API calls
      const suggestion$ = this.newsService.getSuggestionId(suggestionId);
      const history$ = this.newsService.getSuggestionHistory(suggestionId);

      // First get suggestion and history in parallel
      forkJoin({
        suggestion: suggestion$,
        history: history$,
      })
        .pipe(
          finalize(() => {
            // This will run regardless of success or error
            // console.log('Initial data load complete');
          })
        )
        .subscribe({
          next: (results) => {
            // Process suggestion data
            const suggestionData = results.suggestion;

            // Set history data
            this.dataSource.data = results.history;

            // Now get employee data to map author names
            this.usersService
              .getEmployees()
              .pipe(
                finalize(() => {
                  // Always hide loader when everything is complete
                  this.loaderService.hideLoader();
                  // console.log('All data loaded successfully');
                  // this.openSnackBar('Sugerencia cargada correctamente.');
                })
              )
              .subscribe({
                next: (employees) => {
                  const userMap = new Map<number, string>();
                  employees.forEach((employee) => {
                    userMap.set(employee.id, employee.user.username);
                  });

                  this.element = {
                    ...suggestionData,
                    authorId: suggestionData.author,
                    author:
                      userMap.get(suggestionData.author) ||
                      `User ${suggestionData.author}`,
                  };

                  // Set the default values of the form controls based on element data
                  this.suggestionForm.patchValue({
                    toppings: this.toppingList.includes(this.element.status)
                      ? this.element.status
                      : '',
                    // observation: this.element.reason || this.element.content || ''
                  });

                  // console.log('Form values:', {
                  //   toppings: this.suggestionForm.get('toppings')?.value,
                  //   observation: this.suggestionForm.get('observation')?.value,
                  //   elementData: {
                  //     content: this.element.content,
                  //     reason: this.element.reason,
                  //     status: this.element.status,
                  //   },
                  // });
                },
                error: (error) => {
                  console.error('Error fetching employees:', error);

                  // Still set element even if employee lookup fails
                  this.element = {
                    ...suggestionData,
                    authorId: suggestionData.author,
                    author: `User ${suggestionData.author}`,
                  };

                  // Set the default values of the form controls based on element data
                  this.suggestionForm.patchValue({
                    toppings: this.toppingList.includes(this.element.status)
                      ? this.element.status
                      : '',
                    observation:
                      this.element.reason || this.element.content || '',
                  });

                  // console.log('Form values (error case):', {
                  //   toppings: this.suggestionForm.get('toppings')?.value,
                  //   observation: this.suggestionForm.get('observation')?.value,
                  //   elementData: {
                  //     content: this.element.content,
                  //     reason: this.element.reason,
                  //     status: this.element.status,
                  //   },
                  // });
                },
              });
          },
          error: (error) => {
            this.loaderService.hideLoader();
            console.error('Error loading data:', error);
            this.openSnackBar('Error al cargar los datos.');
          },
        });
    });
  }

  saveChanges() {
    // Use our custom validation method which ensures errors are displayed
    const isValid = this.validateForm();

    // Don't proceed if the form is invalid
    if (!isValid) {
      this.openSnackBar(
        'Por favor, complete todos los campos requeridos correctamente.'
      );
      return;
    }

    this.loaderService.showLoader();

    // Prefer HTML content from Quill editor to preserve emojis
    let observationContent =
      this.suggestionForm.get('observation')?.value || '';
    try {
      const quillEditor = document.querySelector('.ql-editor') as HTMLElement;
      if (quillEditor && quillEditor.innerHTML) {
        observationContent = quillEditor.innerHTML;
      }
    } catch (err) {
      // fallback to form value
    }

    this.newsService
      .updateSuggestion(this.element.id, {
        status: this.suggestionForm.get('toppings')?.value || '',
        title: this.element.title,
        content: this.element.content,
        reason: observationContent,
      })
      .subscribe({
        next: (data) => {
          this.loaderService.hideLoader();
          // console.log('Data updated:', data);
          this.ngOnInit();
          this.router.navigate(['/home/communication/mailbox']);
          this.openSnackBar('Sugerencia actualizada correctamente.');
        },
        error: (error) => {
          this.loaderService.hideLoader();
          console.error('Error updating data:', error);
          this.openSnackBar(
            'Error al actualizar la sugerencia. Inténtalo de nuevo.'
          );
        },
      });
  }

  /**
   * Validates the form manually before submission
   * This ensures all errors are shown to the user
   */
  validateForm(): boolean {
    // Mark all fields as touched to trigger validation display
    Object.keys(this.suggestionForm.controls).forEach((key) => {
      const control = this.suggestionForm.get(key);
      control?.markAsTouched();
      control?.updateValueAndValidity();
    });

    // Force change detection to update the view
    setTimeout(() => {
      // This timeout helps ensure the UI updates with validation messages
    }, 0);

    return this.suggestionForm.valid;
  }

  /**
   * Handle editor content changes and trigger validation
   * @param event The editor change event
   */
  onEditorChange(event: any): void {
    // Mark the observation field as touched to trigger validation display
    this.suggestionForm.get('observation')?.markAsTouched();
  }

  /**
   * Called when the editor is created
   * @param editor The Quill editor instance
   */
  onEditorCreated(editor: any): void {
    // Ensure editor has content
    if (editor && this.element && this.element.content) {
      // Set timeout to allow editor to fully initialize
      setTimeout(() => {
        this.suggestionForm.patchValue({
          observation: this.element.content,
        });
      }, 0);
    }
  }

  ngOnDestroy(): void {
    // Ensure loader is hidden when component is destroyed
    this.loaderService.hideLoader();

    // Clean up subscriptions
    this.destroy$.next();
    this.destroy$.complete();
  }
}
